import api from './api';

export const importProducts = async (params, onProgress) => {
  const token = localStorage.getItem('token');
  const response = await fetch('/api/products/import', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });

  if (!response.ok && !response.body) {
    throw new Error('Import failed');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let lastMessage = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop(); // keep incomplete line in buffer

    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const msg = JSON.parse(line);
        lastMessage = msg;
        if (onProgress) onProgress(msg);
      } catch {
        // ignore parse errors
      }
    }
  }

  return lastMessage;
};

export const importProductsCsv = async (rows, onProgress, overwrite = false) => {
  const token = localStorage.getItem('token');
  const response = await fetch('/api/products/import-csv', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ rows, overwrite }),
  });

  if (!response.ok && !response.body) {
    throw new Error('CSV import failed');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let lastMessage = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const msg = JSON.parse(line);
        lastMessage = msg;
        if (onProgress) onProgress(msg);
      } catch {
        // ignore
      }
    }
  }

  return lastMessage;
};

export const getProducts = async (page = 1, limit = 25, filters = {}) => {
  const params = { page, limit };
  if (filters.search) params.search = filters.search;
  if (filters.product_type) params.product_type = filters.product_type;
  if (filters.sync_status) params.sync_status = filters.sync_status;
  if (filters.store_id) params.store_id = filters.store_id;
  if (filters.product_status) params.product_status = filters.product_status;
  if (filters.manufacturer_id) params.manufacturer_id = filters.manufacturer_id;
  const { data } = await api.get('/products', { params });
  return data;
};

export const getProduct = async (id) => {
  const { data } = await api.get(`/products/${id}`);
  return data;
};

export const updateProduct = async (id, updates) => {
  const { data } = await api.put(`/products/${id}`, updates);
  return data;
};

export const deleteProduct = async (id) => {
  const { data } = await api.delete(`/products/${id}`);
  return data;
};

export const deleteAllProducts = async () => {
  const { data } = await api.delete('/products/all');
  return data;
};

export const setOverride = async (productId, storeId, field_name, value) => {
  const { data } = await api.put(`/products/${productId}/overrides/${storeId}`, {
    field_name,
    value,
  });
  return data;
};

export const setMetafieldValue = async (productId, field_id, store_id, value) => {
  const { data } = await api.put(`/products/${productId}/metafields`, {
    field_id,
    store_id,
    value,
  });
  return data;
};

export const updateProductLinks = async (productId, links) => {
  const { data } = await api.put(`/products/${productId}/links`, links);
  return data;
};

export const updateProductVariants = async (productId, variants, variant_groups, variant_sub_groups, variant_sizes) => {
  const { data } = await api.put(`/products/${productId}/variants`, { variants, variant_groups, variant_sub_groups, variant_sizes });
  return data;
};

export const updateVariantFieldValues = async (productId, values) => {
  const { data } = await api.put(`/products/${productId}/variant-field-values`, { values });
  return data;
};

export const updateAdditionalOptions = async (productId, options) => {
  const { data } = await api.put(`/products/${productId}/additional-options`, { options });
  return data;
};

export const searchShopifyProducts = async (productId, storeId, query) => {
  const { data } = await api.get(`/products/${productId}/search-shopify`, { params: { store_id: storeId, query } });
  return data;
};

export const getShopifyImages = async (productId, storeId) => {
  const { data } = await api.get(`/products/${productId}/shopify-images`, { params: storeId ? { store: storeId } : {} });
  return data.images || [];
};

export const matchProduct = async (productId, storeId, shopifyProductId) => {
  const { data } = await api.put(`/products/${productId}/match`, { store_id: storeId, shopify_product_id: shopifyProductId });
  return data;
};

export const unmatchProduct = async (productId, storeId) => {
  const { data } = await api.put(`/products/${productId}/unmatch`, { store_id: storeId });
  return data;
};

export const updateShopifyStatus = async (productId, storeId, shopifyStatus) => {
  const { data } = await api.put(`/products/${productId}/shopify-status`, { store_id: storeId, shopify_status: shopifyStatus });
  return data;
};

export const getProductTypes = async () => {
  const { data } = await api.get('/products/types');
  return data;
};

export const getFamilyIds = async () => {
  const { data } = await api.get('/products/family-ids');
  return data;
};

export const bulkUpdateProductStoreStatus = async (productIds, storeId, status) => {
  const { data } = await api.put('/products/bulk-store-status', { product_ids: productIds, store_id: storeId, status });
  return data;
};

export const createProduct = async (product) => {
  const { data } = await api.post('/products', product);
  return data;
};

export const exportProductsJson = async () => {
  const { data } = await api.get('/products/export-json');
  return data;
};
